import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser, useStackApp } from '@stackframe/react';
import LiquidGradient from './LiquidGradient';
import './LandingPage.css';

export default function LandingPage() {
  const navigate = useNavigate();
  const user = useUser();
  const stackApp = useStackApp();

  const handleSignIn = () => {
    if (user) {
      navigate('/dashboard');
    } else {
      void stackApp.redirectToSignIn();
    }
  };

  const handleGetStarted = () => {
    if (user) {
      navigate('/dashboard');
    } else {
      void stackApp.redirectToSignUp();
    }
  };

  return (
    <div className="landing">
      {/* WebGL liquid gradient background */}
      <LiquidGradient />

      {/* Navigation */}
      <header className="landing-header">
        <div className="landing-logo">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#a3e635" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8 0 5.5-4.78 10-10 10Z"/>
            <path d="M2 21c0-3 1.85-5.36 5.08-6C9.5 14.52 12 13 13 12"/>
          </svg>
          <span>FLORAGUARD</span>
        </div>
        <nav className="landing-nav">
          <a href="#features">Features</a>
          <a href="#how">How It Works</a>
          <a href="#pricing">Pricing</a>
        </nav>
        <button onClick={handleSignIn} className="landing-cta-sm">{user ? 'Dashboard' : 'Sign In'}</button>
      </header>

      {/* Hero */}
      <section className="landing-hero">
        <div className="hero-badge">
          <span className="hero-badge-dot" />
          Trusted by 40+ licensed facilities
        </div>
        <h1 className="hero-title">
          Precision climate control<br/>
          <span className="hero-accent">for modern cultivation</span>
        </h1>
        <p className="hero-sub">
          Monitor every sensor, automate every schedule, and audit every room —
          from a single pane of glass. Built for operators who think in VPD, not dashboards.
        </p>
        <div className="hero-actions">
          <button onClick={handleGetStarted} className="btn-primary">Get Started</button>
          <a href="#features" className="btn-ghost">See Features</a>
        </div>

        {/* Hero visual — abstract facility dashboard mockup */}
        <div className="hero-visual">
          <div className="hv-window">
            <div className="hv-titlebar">
              <span className="hv-dot" /><span className="hv-dot" /><span className="hv-dot" />
            </div>
            <div className="hv-content">
              <div className="hv-sidebar">
                <div className="hv-sidebar-item active" />
                <div className="hv-sidebar-item" />
                <div className="hv-sidebar-item" />
                <div className="hv-sidebar-item" />
                <div className="hv-sidebar-item" />
              </div>
              <div className="hv-main">
                <div className="hv-grid">
                  <div className="hv-card hv-card--lg">
                    <div className="hv-card-label">VPD</div>
                    <div className="hv-card-value">1.24</div>
                    <div className="hv-sparkline">
                      <svg viewBox="0 0 100 30" preserveAspectRatio="none">
                        <polyline points="0,25 10,22 20,18 30,20 40,15 50,12 60,14 70,10 80,8 90,11 100,7" fill="none" stroke="#a3e635" strokeWidth="2"/>
                      </svg>
                    </div>
                  </div>
                  <div className="hv-card">
                    <div className="hv-card-label">Temp</div>
                    <div className="hv-card-value">78°F</div>
                  </div>
                  <div className="hv-card">
                    <div className="hv-card-label">RH</div>
                    <div className="hv-card-value">62%</div>
                  </div>
                  <div className="hv-card">
                    <div className="hv-card-label">CO₂</div>
                    <div className="hv-card-value">1,200</div>
                  </div>
                  <div className="hv-card hv-card--wide">
                    <div className="hv-card-label">Room F9 — Day 34 Flower</div>
                    <div className="hv-bar-row">
                      <div className="hv-bar" style={{width:'85%'}} />
                      <div className="hv-bar hv-bar--dim" style={{width:'62%'}} />
                      <div className="hv-bar hv-bar--accent" style={{width:'94%'}} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="landing-features" id="features">
        <h2 className="section-title">Built for cultivation operators</h2>
        <p className="section-sub">Not another generic IoT dashboard. Every pixel designed for how growers actually work.</p>
        <div className="features-grid">
          <div className="feature-card">
            <div className="feature-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18"/><path d="M9 21V9"/></svg>
            </div>
            <h3>Floor Plan View</h3>
            <p>See your entire facility at a glance. Click any room to drill into live sensor readings and equipment state.</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20V10"/><path d="M18 20V4"/><path d="M6 20v-4"/></svg>
            </div>
            <h3>Sensor Service Linking</h3>
            <p>Connect equipment directly to cage sensors. Set differentials, schedules, and watch the system respond in real time.</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>
            </div>
            <h3>Calibration Workflows</h3>
            <p>Structured audit workflows for every sensor. Track calibration history, flag drift, and never miss a maintenance window.</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
            </div>
            <h3>Equipment Modes</h3>
            <p>Manual On, Manual Off, or Automatic — per unit. Override when you need to, let the system handle the rest.</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
            </div>
            <h3>Schedule Management</h3>
            <p>Light cycles, irrigation, CO₂ injection — all managed from one calendar. Day/night transitions handled automatically.</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
            </div>
            <h3>Alarm & Resolve</h3>
            <p>Instant alerts when readings drift out of spec. Structured resolution workflows so nothing gets forgotten.</p>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="landing-how" id="how">
        <h2 className="section-title">Three steps to full visibility</h2>
        <div className="how-steps">
          <div className="how-step">
            <div className="step-num">01</div>
            <h3>Map your facility</h3>
            <p>Upload your floor plan and define rooms. FloraGuard auto-generates controls panels for every space.</p>
          </div>
          <div className="how-step">
            <div className="step-num">02</div>
            <h3>Connect your sensors</h3>
            <p>Link cage sensors to equipment. Define setpoints, differentials, and service schedules for each unit.</p>
          </div>
          <div className="how-step">
            <div className="step-num">03</div>
            <h3>Operate with confidence</h3>
            <p>Monitor live readings, respond to alarms, and run calibration audits — all from one interface.</p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="landing-cta-section">
        <div className="cta-box">
          <h2>Ready to take control?</h2>
          <p>Start managing your facility with precision. No credit card required.</p>
          <button onClick={handleGetStarted} className="btn-primary btn-primary--lg">Start Free Trial</button>
        </div>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="footer-logo">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#a3e635" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8 0 5.5-4.78 10-10 10Z"/>
            <path d="M2 21c0-3 1.85-5.36 5.08-6C9.5 14.52 12 13 13 12"/>
          </svg>
          <span>FLORAGUARD</span>
        </div>
        <p className="footer-copy">© 2026 FloraGuard. Built for growers, by growers.</p>
      </footer>
    </div>
  );
}
